import GameTrailer from '@/components/game/GameTrailer';
import { Game } from '@/types/GameType';
import { TrailerType } from '@/types/TrailerType';
import { Box, Grid, Heading, Image, Tabs, Text } from '@chakra-ui/react';
import GameScreenShots from './GameScreenShots';

interface Props {
	game: Game;
	movies?: TrailerType[];
	gameId: string | undefined;
}

const GameContentTabs = ({ game, movies, gameId }: Props) => {
	return (
		<Tabs.Root defaultValue="platforms" variant="outline">
			<Tabs.List
				display="flex"
				overflowX="auto"
				overflowY="hidden"
				pb={1}
				css={{
					'&::-webkit-scrollbar': {
						height: '4px'
					}
				}}
			>
				{[
					{ value: 'description', label: 'Description' },
					{ value: 'platforms', label: 'Platforms' },
					...(movies?.length ? [{ value: 'trailers', label: 'Trailers' }] : []),
					{ value: 'screenshots', label: 'Screenshots' }
				].map((tab) => (
					<Tabs.Trigger
						key={tab.value}
						value={tab.value}
						flexShrink={0}
						px={{ base: 4, md: 6 }}
						py={2}
						fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
						fontWeight="bold"
						textTransform="uppercase"
						letterSpacing="wider"
						_selected={{}}
						transition="all 0.2s"
					>
						{tab.label}
					</Tabs.Trigger>
				))}
			</Tabs.List>

			{/* Content sections remain the same */}
			<Tabs.Content value="description">
				<Heading mb={3} fontSize={{ base: 'xl', md: '2xl' }}>
					Game Description
				</Heading>
				<Text fontWeight="200" opacity="0.9">
					{game?.description_raw}
				</Text>
			</Tabs.Content>

			<Tabs.Content value="platforms">
				<Heading fontSize={{ base: 'xl', md: '2xl' }}>Game Platforms</Heading>
				<Grid
					templateColumns={{ base: '1fr', lg: 'repeat(4, 1fr)' }}
					gap={4}
					mt={4}
				>
					{game?.platforms?.map((item) => (
						<Box key={item?.platform?.id} textAlign="center" color="white">
							<Text fontWeight="600" mb="2">
								{item?.platform?.name}
							</Text>
							<Box height="180px" overflow="hidden">
								<Image
									src={item?.platform?.image_background}
									alt={item?.platform?.name}
									objectFit="cover"
									width="100%"
									height="100%"
								/>
							</Box>
						</Box>
					))}
				</Grid>
			</Tabs.Content>

			{movies && movies.length > 0 && (
				<Tabs.Content value="trailers">
					<Heading mb={3} fontSize={{ base: 'xl', md: '2xl' }}>
						Game Trailers
					</Heading>
					<Grid templateColumns={{ base: '1fr', lg: 'repeat(4, 1fr)' }} gap={4}>
						<GameTrailer gameId={game.slug} />
					</Grid>
				</Tabs.Content>
			)}

			<Tabs.Content value="screenshots">
				<Heading mb={3} fontSize={{ base: 'xl', md: '2xl' }}>
					Game Screenshots
				</Heading>
				{gameId && <GameScreenShots gameId={gameId} />}
			</Tabs.Content>
		</Tabs.Root>
	);
};

export default GameContentTabs;
